import React, { createContext, useContext } from 'react';
import { TreeDataPersistor, TreeManagementPersistor } from './Persistor';

/**
 * Tree content only — the read-only viewer's whole dependency on persistence. Kept
 * separate from tree management so an embedded viewer can supply a
 * {@link import('./StaticTreePersistor').StaticTreePersistor} without stubbing
 * create/rename/delete, and so nothing in the viewer's module graph reaches Supabase.
 */
const PersistorContext = createContext<TreeDataPersistor | null>(null);

/** Tree create/rename/delete/visibility. Only the hosted app provides this. */
const TreeManagementPersistorContext = createContext<TreeManagementPersistor | null>(null);

/**
 * Dumb context provider: it holds the persistor it is given and constructs nothing.
 * The hosted app wraps this in `SupabasePersistorProvider`; an embed passes a static one.
 */
export const PersistorProvider: React.FC<{
	persistor: TreeDataPersistor;
	managementPersistor?: TreeManagementPersistor;
	children: React.ReactNode;
}> = ({ persistor, managementPersistor, children }) => {
	const dataTree = (
		<PersistorContext.Provider value={persistor}>{children}</PersistorContext.Provider>
	);

	if (!managementPersistor) {
		return dataTree;
	}

	return (
		<TreeManagementPersistorContext.Provider value={managementPersistor}>
			{dataTree}
		</TreeManagementPersistorContext.Provider>
	);
};

export function usePersistor(): TreeDataPersistor {
	const persistor = useContext(PersistorContext);
	if (!persistor) {
		throw new Error('usePersistor must be used within a PersistorProvider');
	}
	return persistor;
}

export function useTreeManagementPersistor(): TreeManagementPersistor {
	const persistor = useContext(TreeManagementPersistorContext);
	if (!persistor) {
		throw new Error(
			'useTreeManagementPersistor must be used within a SupabasePersistorProvider',
		);
	}
	return persistor;
}
